import React, {
	useMemo,
	useState,
	useCallback,
	ReactElement,
	Fragment,
	useEffect,
} from 'react'
import clsx from 'clsx'
import { Paper, Link, IconButton, Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { SideBySideMagnifier } from 'react-image-magnifiers'
import { CheckCircle } from '@material-ui/icons'
import {
	Row,
	Col,
	Typography,
	ImageComponent,
	CopyrightAgentLoader,
	Icons,
	SecondaryButton,
	PrimaryButton,
} from '@copyrightagent/basic-components'
import {
	preloadImage,
	copyToClipboard,
	getCurrentListTypeInUrl,
} from 'lib/utils'
import createCase, {
	getScreenshotUrl,
	getOriginalImageUrl,
	getArchiveImageUrl,
	ICase,
	getFormatOccurredDate,
	EListType,
} from 'lib/entities/Case'
import { dark50 } from 'lib/Colors'
import { getListUrlBasedOnListType } from 'routes/RouteUrls'
import { useRouter } from 'routes/CustomBrowserRouter'
import { getCountCases, getNextCase } from '../../routes/Private/InReview/api'
import OtherCasesByDomain from './ByDomain'
import useStyle from './style'
import { SelectRegistrationCertificateModal } from './SelectRegistrationCertificateModal'
import { UploadCertificateModal } from '../../routes/Private/RegistrationCertificates/components/UploadCertificateModal'
import Response from 'lib/http/Response'
import { uploadRegisteredImages } from '../../routes/Private/RegistrationCertificates/api'
import { toast } from '../../store/utils'
import { useRegistrationCertificateStore } from './registrationCertificateStore'
import { usePartnerSettingsStore } from '../../zustand-store/store'
import { SelectTransferAgreementModal } from './SelectTransferAgreementModal'
import { useTransferAgreementStore } from './transferAgreementStore'
import { UploadTransferAgreementModal } from '../../routes/Private/TransferAgreements/components/UploadTransferAgreementModal'
import { uploadTransferAgreementImages } from '../../routes/Private/TransferAgreements/api'

interface IProps {
	theCase: ICase
}

export default function ImageAndScreenshot({ theCase }: IProps): ReactElement {
	const { registrationCertificatesEnabled, transferAgreementsEnabled } =
		usePartnerSettingsStore()
	const {
		selectRegistrationCertificateModalOpened,
		setSelectRegistrationCertificateModalOpened,
		registrationCertificate,
		setRegistrationCertificate,
	} = useRegistrationCertificateStore()
	const {
		selectTransferAgreementModalOpened,
		setSelectTransferAgreementModalOpened,
		transferAgreement,
		setTransferAgreement,
	} = useTransferAgreementStore()
	const { t } = useTranslation()
	const classes = useStyle()
	const router = useRouter()
	const [loaded, setLoaded] = useState<boolean>(false)
	const [domainCaseCount, setDomainCaseCount] = useState<number>(0)

	const domain = theCase.domain
	const listType = getCurrentListTypeInUrl()

	useEffect(() => {
		getCountCases({ listType: EListType.inReview, filters: { domain } }).then(
			(response) => setDomainCaseCount(response.data.count),
		)
	}, [domain, theCase.case_id])

	useEffect(() => {
		if (
			!theCase.registration_certificate_number ||
			!theCase.registration_certificate_id
		) {
			return
		}
		setRegistrationCertificate({
			registrationCertificateId: theCase.registration_certificate_id,
			registrationCertificateNumber: theCase.registration_certificate_number,
		})
	}, [])

	useEffect(() => {
		if (
			!theCase.transfer_agreement_id ||
			!theCase.transfer_agreement_photographer_name
		) {
			return
		}
		setTransferAgreement({
			transferAgreementId: theCase.transfer_agreement_id,
			photographerName: theCase.transfer_agreement_photographer_name,
		})
	}, [])

	const setPreloadedImage = useCallback(async () => {
		const response = await getNextCase(theCase.case_id)
		if (response.isSuccessful && response.data === 200) {
			const newCase = createCase(response.data)
			preloadImage(getScreenshotUrl(newCase, 400))
			preloadImage(getOriginalImageUrl(newCase, 400))
		}
	}, [theCase.case_id])

	const setImageLoaded = (load: boolean) => {
		setLoaded(load)
		setPreloadedImage()
	}

	const handleGoToDomainCases = useCallback(() => {
		const pathname = getListUrlBasedOnListType({
			listType: EListType.inReview,
			filters: { domain },
			resetFilter: true,
		})

		router.history.push(pathname)
	}, [router.history, theCase])

	const renderedImageComponent = useMemo(
		() => (
			<ImageComponent
				className={classes.image}
				src={getOriginalImageUrl(theCase, 400)}
				onLoad={setImageLoaded}
			/>
		),
		[theCase.photo_id],
	)

	const renderedLeftPaper = useMemo(() => {
		if (!theCase.violation_id) return <></>
		return (
			<Paper
				classes={{ root: `${classes.paper} ${classes.imagePaper}` }}
				className={loaded ? classes.visible : classes.hidden}
			>
				<UploadCertificateModal
					uploadSuccessCallback={async (response: Response) => {
						const registrationCertificateId =
							response.data.registration_certificate_id
						const registrationCertificateNumber =
							response.data.registration_certificate_number
						const uploadRegisteredImageResponse = await uploadRegisteredImages({
							partnerImageIds: [theCase.client_image_id],
							registrationCertificateId,
						})
						if (uploadRegisteredImageResponse.isSuccessful) {
							setRegistrationCertificate({
								registrationCertificateId,
								registrationCertificateNumber,
							})
							setSelectRegistrationCertificateModalOpened(false)
						} else {
							await toast(
								'Failed to attached image to registration certificate',
								'error',
							)
						}
					}}
				/>
				<UploadTransferAgreementModal
					uploadSuccessCallback={async (response: Response) => {
						const transferAgreementId =
							response.data.transfer_agreement_id
						const photographerName =
							response.data.photographer_name
						const uploadRegisteredImageResponse = await uploadTransferAgreementImages({
							partnerImageIds: [theCase.client_image_id],
							transferAgreementId,
						})
						if (uploadRegisteredImageResponse.isSuccessful) {
							setTransferAgreement({
								transferAgreementId,
								photographerName,
							})
							setSelectTransferAgreementModalOpened(false)
						} else {
							await toast(
								'Failed to link image to agreement',
								'error',
							)
						}
					}}
				/>
				<SelectRegistrationCertificateModal
					partnerImageId={theCase.client_image_id}
				/>
				<SelectTransferAgreementModal
					partnerImageId={theCase.client_image_id}
				/>
				<Typography
					variant="caption"
					align="center"
					className={`${classes.imageLabel} ${classes.originalImageLabel}`}
				>
					{t('inReview/originalImageLabel')}
				</Typography>
				{renderedImageComponent}

				<Row
					className={classes.infoWrapLeft}
					justify="flex-start"
					align="flex-start"
				>
					<Grid container alignItems="center" spacing={2}>
						<Grid item xs={3}>
							<Typography className={`${classes.textItem} ${classes.bold}`}>
								{t('inReview/image')}:
							</Typography>
						</Grid>
						<Grid item xs={9}>
							<Row justify="flex-start" className={classes.noOverflow}>
								<Link
									href={getArchiveImageUrl(theCase, 1024)}
									target="_blank"
									style={{ textDecoration: 'none' }}
									className={classes.truncate}
								>
									<Row className={classes.noOverflow}>
										<Typography
											className={clsx(
												classes.textItem,
												classes.bold,
												classes.truncate,
											)}
										>
											{theCase.photo_name}
										</Typography>
										<Icons.LinkIcon size="small" />
									</Row>
								</Link>
								<IconButton
									className={classes.iconButton}
									onClick={(e) => copyToClipboard(e, theCase.photo_name || '')}
								>
									<Icons.CopyIcon size="small" color={dark50} />
								</IconButton>
							</Row>
						</Grid>

						<Grid item xs={3}>
							<Typography className={clsx(classes.lightGrey, classes.semiBold)}>
								{t('inReview/photographer')}:
							</Typography>
						</Grid>
						<Grid item xs={9}>
							<Typography>{theCase.photographer_name}</Typography>
						</Grid>

						{theCase.supplier && (
							<>
								<Grid item xs={3}>
									<Typography
										className={clsx(classes.lightGrey, classes.semiBold)}
									>
										{t('inReview/supplier')}
									</Typography>
								</Grid>
								<Grid item xs={9}>
									<Typography>{theCase.supplier}</Typography>
								</Grid>
							</>
						)}

						<Grid item xs={3}>
							<Typography className={clsx(classes.lightGrey, classes.semiBold)}>
								{t('inReview/uploaded')}:
							</Typography>
						</Grid>
						<Grid item xs={9}>
							<Typography className={classes.textItem}>
								{getFormatOccurredDate(theCase.reg_date)}
							</Typography>
						</Grid>

						{theCase.url_raw && (
							<>
								<Grid item xs={3}>
									<Typography
										className={clsx(classes.lightGrey, classes.semiBold)}
									>
										{t('inReview/url')}:
									</Typography>
								</Grid>
								<Grid item xs={9}>
									<Link
										href={theCase.url_raw}
										target="_blank"
										rel="noopener noreferrer"
									>
										<Typography
											className={clsx(classes.textItem, classes.truncate)}
										>
											{theCase.url_raw}
										</Typography>
									</Link>
								</Grid>
							</>
						)}

						{registrationCertificatesEnabled && (
							<>
								<Grid item xs={3}>
									<Typography
										className={clsx(classes.lightGrey, classes.semiBold)}
									>
										Registration Certificate:
									</Typography>
								</Grid>
								<Grid item xs={9}>
									{registrationCertificate ? (
										<div style={{ display: 'flex', gap: 8 }}>
											<Link
												href={`/registration-certificates/${registrationCertificate.registrationCertificateId}`}
												target="_blank"
											>
												<Typography>
													{
														registrationCertificate.registrationCertificateNumber
													}
												</Typography>
											</Link>
											<CheckCircle
												style={{ color: 'green', fontSize: '20px' }}
											/>
										</div>
									) : (
										<>
											<PrimaryButton
												type="button"
												text={'Mark Image As Registered'}
												buttonStyle={{
													defaultCSS: {
														backgroundColor: '#4FA3D9',
													},
													hoverCSS: {
														backgroundColor: '#4A96C5',
													},
												}}
												onClick={() => {
													setSelectRegistrationCertificateModalOpened(true)
												}}
											/>
										</>
									)}
								</Grid>
							</>
						)}

						{transferAgreementsEnabled && (
							<>
								<Grid item xs={3}>
									<Typography
										className={clsx(classes.lightGrey, classes.semiBold)}
									>
										Transfer of Rights Agreement:
									</Typography>
								</Grid>
								<Grid item xs={9}>
									{transferAgreement ? (
										<div style={{ display: 'flex', gap: 8 }}>
											<Link
												href={`/transfer-agreements/${transferAgreement.transferAgreementId}`}
												target="_blank"
											>
												<Typography>
													{transferAgreement.photographerName}
												</Typography>
											</Link>
											<CheckCircle
												style={{ color: 'green', fontSize: '20px' }}
											/>
										</div>
									) : (
										<>
											<PrimaryButton
												type="button"
												text={'Link Image With Agreement'}
												buttonStyle={{
													defaultCSS: {
														backgroundColor: '#4FA3D9',
													},
													hoverCSS: {
														backgroundColor: '#4A96C5',
													},
												}}
												onClick={() => {
													setSelectTransferAgreementModalOpened(true)
												}}
											/>
										</>
									)}
								</Grid>
							</>
						)}
					</Grid>
				</Row>
			</Paper>
		)
	}, [
		theCase,
		classes,
		t,
		loaded,
		selectRegistrationCertificateModalOpened,
		selectTransferAgreementModalOpened,
		registrationCertificate,
		transferAgreement,
	])

	const renderedRightPaper = useMemo(() => {
		if (!theCase.violation_id) return <></>

		const extractedDomain = theCase.domain

		return (
			<Paper
				classes={{ root: `${classes.paper} ${classes.screenshotPaper}` }}
				className={loaded ? classes.visible : classes.hidden}
			>
				<Typography
					variant="caption"
					align="center"
					className={`${classes.imageLabel} ${classes.infringingImageLabel}`}
				>
					{t('inReview/infringementImageLabel')}
				</Typography>
				<div className={classes.relative}>
					<SideBySideMagnifier
						className={classes.image}
						imageSrc={getScreenshotUrl(theCase, 400)}
						alwaysInPlace
					/>
					{domainCaseCount > 1 && (
						<SecondaryButton
							className={classes.domainButton}
							text={t('inReview/casesOnDomain', { domainCaseCount })}
							onClick={handleGoToDomainCases}
						/>
					)}
				</div>
				<Col className={classes.infoWrapRight} justify="space-between">
					<Row align="flex-start" className={classes.infoRow}>
						<Col className={clsx(classes.infoCol, classes.noOverflow)}>
							<Typography className={`${classes.textItem} ${classes.bold}`}>
								{theCase.company_name}
							</Typography>
							<Typography className={clsx(classes.textItem, classes.lightGrey)}>
								{t('inReview/caseFoundAt')}
							</Typography>
							<a
								style={{ textDecoration: 'none' }}
								className={classes.truncate}
								rel="noopener noreferrer"
								href={theCase.url_raw}
								target="_blank"
							>
								<Typography
									className={clsx(
										classes.textItem,
										classes.semiBold,
										classes.truncate,
										classes.paddingRight,
									)}
								>
									{extractedDomain.length > 140
										? `${extractedDomain.slice(0, 140)}...`
										: extractedDomain}
								</Typography>
							</a>
						</Col>
						<OtherCasesByDomain theCase={theCase} />
					</Row>
					<Typography
						fontWeight={600}
						fontColor={dark50}
						style={{ marginBottom: 0 }}
					>
						{theCase.screenshot_timestamp && `${t('inReview/detected')}:`}&nbsp;
						{theCase.screenshot_timestamp}
					</Typography>
				</Col>
			</Paper>
		)
	}, [theCase, classes, t, loaded, domainCaseCount])

	return (
		<Row className={classes.container} align={'flex-start'}>
			{!theCase.violation_id ? (
				<CopyrightAgentLoader className={classes.absoluteCenter} />
			) : (
				<>
					{!loaded && (
						<CopyrightAgentLoader className={classes.absoluteCenter} />
					)}
					{renderedLeftPaper}
					{renderedRightPaper}
				</>
			)}
		</Row>
	)
}
